import {LitElement, html, unsafeCSS, PropertyValues} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import '../bg-clip-paths/bg-quatro-large/bg-quatro-large.ts'
import '../bg-image/bg-image.ts'
import ctaCss from './bg-cta.scss?inline'
import {classMap} from "lit/directives/class-map.js";
import {styleMap} from 'lit/directives/style-map.js';
import {queryAssignedElements} from "lit/decorators/query-assigned-elements.js";
import {getColorScheme, getBlockWidth} from "../../library/design-helpers.ts";


export interface BgCtaInterface {
    image: string;
    heading: string;
    headingSize: number;

    colourScheme: string;
    blockAlignment: string;
    blockWidth: string;
    imageStyle: string;
    contentPosition: string;
    linksPosition: string;
}


@customElement('bg-cta')
export class BgCta extends LitElement {
    constructor(args: BgCtaInterface) {
        super();
        if (!args) {
            return;
        }
        this.image = args.image;
        this.heading = args.heading;
        this.headingSize = args.headingSize

        this.colourScheme = args.colourScheme;
        this.blockAlignment = args.blockAlignment;
        this.blockWidth = args.blockWidth;
        this.imageStyle = args.imageStyle;
        this.contentPosition = args.contentPosition;
        this.linksPosition = args.linksPosition;

    }

    @property()
    docsHint = '';

    @property({type: String})
    image: string = '';

    @property({type: String})
    heading = '';

    @property({type: Number, attribute: 'heading-size'})
    headingSize = 1;

    @property({type: String, attribute: 'colour-scheme'})
    colourScheme = '';

    @property({type: String, attribute: 'block-alignment'})
    blockAlignment = '';

    @property({type: String, attribute: 'block-width'})
    blockWidth = '';

    @property({type: String, attribute: 'image-style'})
    imageStyle = '';

    @property({type: String, attribute: 'content-position'})
    contentPosition = '';

    @property({type: String, attribute: 'links-position'})
    linksPosition = '';

    @queryAssignedElements({slot: 'image'})
    imageSlot!: Array<HTMLElement>;
    @property({type: Array}) imageData: Element[] = [];

    @queryAssignedElements({slot: 'button'})
    ctaButtonItems!: Array<HTMLElement>;
    @property({type: Array}) ctaButtons: Element[] = [];


    render() {
        var ctaMainContainerClasses = {
            "bg-cta": true,
            ...getColorScheme(this.colourScheme),
            ...getBlockWidth(this.blockWidth),

        }

        var ctaContainerClasses = {
            "bg-cta-container": this.imageData.length !== 0 || this.imageStyle !== "background",
            "bg-cta-image-container": this.imageData.length !== 0 && this.imageStyle === "background",
            "bg-cta-content-left": this.contentPosition.includes("left"),
            "bg-cta-row": this.contentPosition.includes("right"),
        }
        
        var ctaColumnContent = {
            "bg-cta-column": this.imageStyle !== "background" && this.imageData.length !== 0,
            "bg-cta-content-background-image": this.imageData.length !== 0 && this.imageStyle === "background",
            "bg-cta-only-text": this.imageData.length === 0,
            ...this.getContentPosition(this.contentPosition, this.imageStyle)
        }

        var ctaColumnImage = {
            "bg-cta-column": this.imageStyle !== "background",
            "bg-cta-background-image": this.imageStyle === "background",
            "bg-cta-no-display": this.imageData.length == 0
        }

        var ctaContent = {
            "bg-cta-content": this.imageStyle !== "background",
            "bg-cta-background-content": this.imageStyle === "background",
            "bg-align-text-centre": this.blockAlignment === "Centre",
            "bg-align-text-left": this.blockAlignment === "Left",
            "bg-align-text-right": this.blockAlignment === "Right",
            "bg-align-items-centre": this.blockAlignment === "Centre",
            "bg-align-items-left": this.blockAlignment === "Left",
            "bg-align-items-right": this.blockAlignment === "Right",
            "bg-cta-button-before": this.linksPosition === "before-text",
            "bg-cta-button-after": this.linksPosition === "after-text",
            "bg-cta-button-above": this.linksPosition === "above-text",
            "bg-cta-button-below": this.linksPosition.startsWith("below-text"),
            "bg-cta-button-bottom": this.linksPosition === "below-text-bottom"
        }

        var buttonAlignment = {
            "bg-cta-buttons": true,
            "bg-cta-no-display": this.ctaButtons.length === 0
        }

        var backgroundImageStyles = {}

        return html`
            <div part="base" class="${classMap(ctaMainContainerClasses)}">
                <div>
                    <div class="${classMap(ctaContainerClasses)}" style=${styleMap(backgroundImageStyles)}>
                        <div class="${classMap(ctaColumnImage)}">
                            ${this.getStyledImage(this.imageStyle)}
                        </div>
                        <div part="content" class="${classMap(ctaColumnContent)}">
                            <div class="${classMap(ctaContent)}">
                                <div class="bg-cta-text-content">
                                    <slot name="heading"></slot>
                                    <slot name="content"></slot>
                                    <slot></slot>
                                </div>
                                <div class=${classMap(buttonAlignment)}>
                                    <slot name="button"></slot>
                                    ${this.ctaButtons?.map((el: any) =>
                                            html`${el}`
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    static styles = [unsafeCSS(ctaCss)];

    getStyledImage(imageStyle: string) {
        if (imageStyle == "masked") {
            return html`
                <div class="bg-cta-image-masked">
                    <bg-quatro-large colourScheme="${this.colourScheme}">
                        <slot name="image"></slot>
                    </bg-quatro-large>
                </div>
            `

        } else {
            return html`
                <slot name="image"></slot>`;
        }
    }


    firstUpdated = () => {
        this.waitForSlots();
    }

    async waitForSlots() {
        //Slots are loading after update functions run. This one is for waiting until slots are loaded.
        let retries = 5;
        while ((!this.ctaButtonItems?.length || !this.imageSlot?.length) && retries > 0) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            retries--;
        }
        this.updateBackgroundImage();
        this.updateButtons();
        this.updateStyles()

    }

    updateStyles() {
        // styling inside the rich text
        const slots = this.shadowRoot?.querySelectorAll('slot');
        if (slots) {
            slots.forEach(function (value) {
                const assignedNodes = value.assignedNodes({flatten: true}) as HTMLElement[];
                assignedNodes.forEach(node => {
                    if (node instanceof HTMLElement) {
                        const links = node.querySelectorAll('a');
                        links.forEach(link => {
                            link.style.color = 'var(--bg-link-color)';
                            link.style.textDecoration = 'underline';
                        });
                    }
                });
            })
        }
    }

    updateBackgroundImage() {
        if (this.imageSlot) {
            this.imageData = Array.from(this.imageSlot);
        }
    }

    updateButtons() {
        if (this.ctaButtonItems) {
            this.ctaButtons = Array.from(this.ctaButtonItems);
        }
    }

    getContentPosition = (value: string, imageStyle: string) => {
        var prefix = imageStyle === "background" ? "bg-background-text-" : "bg-text-content-"
        var contentPositionClass = prefix + `${value.toLowerCase().replace(' ', '-')}`
        const result = {};
        result[contentPositionClass] = true;
        return result;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'bg-cta': BgCta;
    }
}

